import React from "react";
import { X } from "lucide-react";

import { COMPANY_INFO } from "../../constants/company";


export function PolicyModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal policy-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body policy-modal-body">
          <div className="policy-copy">{children}</div>
        </div>
      </div>
    </div>
  );
}


export function PrivacyContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>
      <p>AI Music Maker ("we", "us", "our") respects your privacy. This Policy explains how we collect, use, and protect your information when you use our website and services.</p>

      <h3>Information We Collect</h3>
      <ul>
        <li><strong>Account data:</strong> name, email address (collected via Google Sign-In)</li>
        <li><strong>Usage data:</strong> actions within the app, generated content metadata, timestamps</li>
        <li><strong>Payment data:</strong> handled securely by our payment processor Paddle.com; we do not store or have access to your full card numbers or payment details</li>
      </ul>

      <h3>How We Use Information</h3>
      <ul>
        <li>To provide, maintain, and improve our AI song generation services</li>
        <li>To process payments and manage subscriptions through Paddle.com</li>
        <li>To communicate important updates, respond to support requests, and send service-related notifications</li>
        <li>To analyze usage patterns and improve user experience</li>
      </ul>

      <h3>Data Retention</h3>
      <p>We retain personal data for as long as your account is active or as needed to provide our services and comply with legal obligations. You may request deletion of your data at any time.</p>

      <h3>Third-Party Services</h3>
      <p>We use the following trusted third-party providers:</p>
      <ul>
        <li><strong>Paddle.com:</strong> Payment processing and subscription management. Paddle is the Merchant of Record for all transactions. View their privacy policy at <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">paddle.com/legal/privacy</a></li>
        <li><strong>Google:</strong> Authentication services</li>
        <li><strong>AI Music API providers:</strong> Song and lyrics generation</li>
      </ul>

      <h3>Your Rights (GDPR)</h3>
      <p>If you are in the EU/EEA, you have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion of your data ("right to be forgotten")</li>
        <li>Object to or restrict processing of your data</li>
        <li>Data portability</li>
      </ul>
      <p>To exercise these rights, contact us using the details below.</p>

      <h3>Security</h3>
      <p>We implement industry-standard administrative, technical, and physical safeguards to protect your data, including SSL/TLS encryption. However, no method of transmission over the Internet is 100% secure.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For privacy-related questions or requests:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
        </div>
      </div>
    </>
  );
}


export function TermsContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>

      <h3>Agreement</h3>
      <p>By accessing or using AI Music Maker (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.</p>

      <h3>Service Description</h3>
      <p>AI Music Maker is a web-based software service that uses artificial intelligence to generate songs and lyrics based on user prompts. The Service enables users to:</p>
      <ul>
        <li>Generate AI-composed songs from text descriptions or custom lyrics</li>
        <li>Generate AI-written lyrics from descriptions</li>
        <li>Generate AI music videos and clips</li>
        <li>Download and use generated content</li>
      </ul>
      <p><strong>Limitations:</strong> Generated content quality may vary. The AI may not always produce desired results. Generation times typically range from 30 seconds to 2 minutes.</p>

      <h3>Payment Processing - Paddle.com</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>
      <p>All payments, billing, and subscription management are handled by Paddle. By making a purchase, you also agree to Paddle's <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noopener noreferrer">Buyer Terms</a>.</p>

      <h3>Accounts</h3>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate information and promptly update it if it changes.</p>

      <h3>Subscriptions & Billing</h3>
      <ul>
        <li>Subscriptions renew automatically at the end of each billing period unless canceled</li>
        <li>You may cancel your subscription at any time; access continues until the end of the current billing period</li>
        <li>Applicable taxes will be added based on your location</li>
        <li>Price changes will be communicated in advance</li>
      </ul>

      <h3>Refunds</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> for subscription purchases. See our Refund Policy for details.</p>

      <h3>Acceptable Use</h3>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service to generate content that is illegal, harmful, threatening, abusive, defamatory, or infringes on intellectual property rights</li>
        <li>Attempt to interfere with the normal operation of the Service</li>
        <li>Access the Service using automated methods other than our provided interface</li>
        <li>Resell or redistribute the Service without authorization</li>
        <li>Use the Service to create content that impersonates real individuals without consent</li>
      </ul>

      <h3>Intellectual Property</h3>
      <p>Subject to your compliance with these Terms:</p>
      <ul>
        <li>You retain rights to your original input (prompts, custom lyrics)</li>
        <li>You receive a license to use AI-generated content for personal or commercial purposes</li>
        <li>We retain rights to the underlying AI models and Service technology</li>
      </ul>
      <p>Generated content may not be unique; similar outputs may be generated for other users.</p>

      <h3>Disclaimer of Warranties</h3>
      <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT GUARANTEE UNINTERRUPTED, ERROR-FREE, OR SECURE OPERATION.</p>

      <h3>Limitation of Liability</h3>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.</p>

      <h3>Changes to Terms</h3>
      <p>We may update these Terms from time to time. We will notify you of material changes. Continued use after changes constitutes acceptance.</p>

      <h3>Governing Law</h3>
      <p>These Terms are governed by applicable laws. Any disputes shall be resolved through appropriate legal channels.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For questions about these Terms:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
        </div>
      </div>
    </>
  );
}


export function RefundContent() {
  return (
    <>
      <p className="policy-updated">Last updated: {new Date().toISOString().slice(0,10)}</p>

      <h3>Payment Processing</h3>
      <p><strong>Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</strong></p>

      <h3>30-Day Money-Back Guarantee</h3>
      <p>We offer a <strong>30-day money-back guarantee</strong> on all subscription purchases. If you are not satisfied with our Service within the first 30 days of your subscription, you may request a full refund.</p>
      <p>To request a refund within the 30-day period:</p>
      <ul>
        <li>Contact us at <strong>{COMPANY_INFO.email}</strong> with your order details</li>
        <li>Or contact Paddle support directly at <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">paddle.com/support</a></li>
      </ul>

      <h3>Subscription Cancellations</h3>
      <p>You may cancel your subscription at any time:</p>
      <ul>
        <li>Access to the Service continues until the end of your current billing period</li>
        <li>No partial refunds are provided for unused time after the 30-day guarantee period</li>
        <li>To cancel, use the "Manage subscription" button in your account or contact support</li>
      </ul>

      <h3>One-Time Credit Purchases</h3>
      <ul>
        <li>Credit purchases are final once credits have been used</li>
        <li>If purchased in error and credits are unused, contact support within 14 days for a refund</li>
      </ul>

      <h3>Chargebacks</h3>
      <p>Please contact {COMPANY_INFO.email} before initiating a chargeback. We're happy to help resolve billing issues.</p>

      <h3>Contact Us</h3>
      <div className="contact-section">
        <p>For refund requests or billing questions:</p>
        <div className="contact-card">
          <h4>Company Information</h4>
          <p className="company-name">{COMPANY_INFO.name}</p>
          <p className="company-address">{COMPANY_INFO.address}</p>
          <p className="company-email">
            <strong>Support:</strong> <a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a>
          </p>
          <p className="company-email" style={{marginTop: '8px'}}>
            <strong>Paddle Support:</strong> <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">paddle.com/support</a>
          </p>
        </div>
      </div>
      <p className="policy-note">Please include your order ID or the email address used for purchase when contacting support.</p>
    </>
  );
}


export function HowItWorksContent() {
  return (
    <>
      <h3>Step 1: Write Your Lyrics</h3>
      <p>Paste your lyrics or start from a prompt. You can also use our "Inspire Me" feature to get creative suggestions.</p>
      <h3>Step 2: Generate</h3>
      <p>Click Generate — our AI composes vocals and music based on your input and selected style.</p>
      <h3>Step 3: Download & Share</h3>
      <p>Preview your song, download it in high quality, and share it with the world.</p>
      <h3>Advanced Features</h3>
      <ul>
        <li><strong>Generate Clip:</strong> Create AI-powered music videos from your songs</li>
        <li><strong>AI Covers:</strong> Generate covers with custom voice models</li>
        <li><strong>Video Generation:</strong> Turn your songs into engaging visual content</li>
      </ul>
    </>
  );
}


export function FAQContent() {
  return (
    <>
      <div className="faq-list">
        <div className="faq-item">
          <h3>What is AI Music Maker?</h3>
          <p>AI Music Maker is an advanced AI-powered platform that generates original songs, lyrics, and music videos from your prompts and ideas.</p>
        </div>

        <div className="faq-item">
          <h3>How do credits work?</h3>
          <p>Credits are used to generate content. Song generation costs 2 credits, video generation costs vary based on duration (1 credit per 3 seconds), and clips use credits based on total video length.</p>
        </div>

        <div className="faq-item">
          <h3>Can I use the generated music commercially?</h3>
          <p>Yes! All content you generate belongs to you and can be used for personal or commercial purposes, subject to our terms of service.</p>
        </div>

        <div className="faq-item">
          <h3>What music styles are available?</h3>
          <p>We support a wide range of genres including Pop, Rock, Electronic, Hip-Hop, Jazz, Classical, R&B, Country, Metal, and Ambient.</p>
        </div>

        <div className="faq-item">
          <h3>How long does generation take?</h3>
          <p>Song generation typically takes 1-3 minutes. Video and clip generation may take longer depending on length and complexity.</p>
        </div>

        <div className="faq-item">
          <h3>What payment methods do you accept?</h3>
          <p>We accept all major credit cards, PayPal, and other payment methods through our secure payment processor Paddle.</p>
        </div>

        <div className="faq-item">
          <h3>Can I get a refund?</h3>
          <p>Yes, we offer a 30-day money-back guarantee on all subscription purchases. Contact our support team for assistance.</p>
        </div>

        <div className="faq-item">
          <h3>How do I contact support?</h3>
          <p>You can reach our support team at <a href="mailto:support@music-producer.ai">support@music-producer.ai</a>. We typically respond within 24 hours.</p>
        </div>
      </div>
    </>
  );
}


export function PricingContent({ onClose, onOpenPricing }) {
  return (
    <>
      <p className="subtle text-center mb-6">Choose the plan that works best for you</p>
      <div className="pricing-cards-modal">
        <div className="pricing-card-modal">
          <h3>Free</h3>
          <div className="price">$0</div>
          <ul>
            <li>1 free song on signup</li>
            <li>Basic song generation</li>
          </ul>
        </div>
        <div className="pricing-card-modal featured">
          <div className="popular-badge">Most Popular</div>
          <h3>Monthly</h3>
          <div className="price">$39.99<span>/month</span></div>
          <ul>
            <li>100 credits/month</li>
            <li>Full song generation</li>
            <li>Video generation</li>
            <li>More music styles</li>
            <li>Duets & Karaoke</li>
            <li>Clip generation</li>
            <li>Covers & Voice Models</li>
          </ul>
        </div>
        <div className="pricing-card-modal">
          <div className="save-badge">SAVE 20%</div>
          <h3>Yearly</h3>
          <div className="price">$383.99<span>/year</span></div>
          <ul>
            <li>Everything in Monthly</li>
            <li>Best value!</li>
          </ul>
        </div>
      </div>
      {onOpenPricing && (
        <div className="text-center mt-6">
          <button className="btn-primary" onClick={onOpenPricing}>
            View Plans & Subscribe
          </button>
        </div>
      )}
    </>
  );
}
